<template>
  <div class="card">
    <div class="card-header d-flex justify-content-between align-items-center">
      <div>
        <h3 class="mb-0">Ledger</h3>
        <small class="form-text text-muted">Append-only. Every amount is already the all-in figure the rider was charged.</small>
      </div>
      <a class="btn btn-outline-primary btn-sm" :href="exportUrl" target="_blank" rel="noopener">
        <i class="fa-solid fa-file-csv"></i> Export CSV
      </a>
    </div>
    <div class="card-body">
      <div v-if="isLoading" class="text-center py-3">
        <span class="spinner-border spinner-border-sm me-2"></span> Loading...
      </div>
      <div v-else-if="lines.length === 0" class="text-muted">No ledger activity yet.</div>
      <div v-else class="table-responsive">
        <table class="table table-borderless">
          <thead>
          <tr>
            <th>Date</th>
            <th>Type</th>
            <th>Category</th>
            <th>Direction</th>
            <th>Amount</th>
            <th>Balance After</th>
            <th>Description</th>
            <th>Reference</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="line in lines" :key="line.id">
            <td>{{ formatDate(line.effectiveAt) }}</td>
            <td v-html="walletTransactionType(line.transactionType)"></td>
            <td>{{ line.accountType || '—' }}</td>
            <td>{{ line.direction }}</td>
            <td v-html="minorCurrency(line.signedAmountMinor)"></td>
            <td v-html="minorCurrency(line.balanceAfterMinor)"></td>
            <td>{{ line.description || '' }}</td>
            <td class="text-muted small">{{ line.referenceType || '' }} {{ line.referenceId || '' }}</td>
          </tr>
          </tbody>
        </table>
      </div>

      <nav v-if="totalPages > 1" class="d-flex justify-content-between align-items-center mt-2">
        <button class="btn btn-outline-secondary btn-sm" :disabled="page <= 1" @click="changePage(page - 1)">Previous</button>
        <span class="text-muted small">Page {{ page }} of {{ totalPages }} ({{ totalRows }} rows)</span>
        <button class="btn btn-outline-secondary btn-sm" :disabled="!hasNext" @click="changePage(page + 1)">Next</button>
      </nav>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { minorCurrency, walletTransactionType } from '../utility/DatatableRenderer';

const props = defineProps({
  walletId: { type: String, required: true }
});

const lines = ref([]);
const page = ref(1);
const totalPages = ref(0);
const totalRows = ref(0);
const hasNext = ref(false);
const isLoading = ref(false);

const exportUrl = computed(() => `/api/admin/wallets/${props.walletId}/statement.csv`);

const formatDate = (value) => value ? new Date(value).toLocaleString() : '';

const load = async () => {
  isLoading.value = true;
  try {
    const response = await fetch(`/api/admin/wallets/${props.walletId}/transactions?page=${page.value}`);
    if (response.ok) {
      const data = await response.json();
      lines.value = data.lines || [];
      totalPages.value = data.totalPages || 0;
      totalRows.value = data.totalRows || 0;
      hasNext.value = !!data.hasNext;
    }
  } catch (error) {
    console.error('Error loading wallet ledger:', error);
  } finally {
    isLoading.value = false;
  }
};

const changePage = (next) => {
  if (next < 1) return;
  page.value = next;
  load();
};

onMounted(load);

defineExpose({ load });
</script>
